import { Controller } from "@hotwired/stimulus"

// Reloads a turbo-frame src from form field params (audience / notification previews).
//
// Mark inputs with target="param" and data-turbo-frame-query-preview-name-param="audience_status".
// Optional: data-turbo-frame-query-preview-event-param="change"|"input" (defaults by control type).
// Optional confirm-message swap when a named checkbox is checked (announcement publish confirm).
export default class extends Controller {
  static targets = ["frame", "param", "confirmMessage"]

  static values = {
    previewUrl: String,
    debounce: { type: Number, default: 400 },
    defaultConfirmMessage: String,
    alternateConfirmMessage: String,
    alternateWhenParam: String
  }

  connect () {
    this.boundHandlers = new Map()

    this.paramTargets.forEach((el) => {
      const eventName = this.eventNameFor(el)
      const handler = eventName === "input"
        ? () => this.scheduleRefresh()
        : () => {
          this.refreshPreview()
          this.updateConfirmMessage()
        }
      el.addEventListener(eventName, handler)
      this.boundHandlers.set(el, { eventName, handler })
    })

    this.updateConfirmMessage()
  }

  disconnect () {
    clearTimeout(this.refreshTimeout)
    this.boundHandlers.forEach(({ eventName, handler }, el) => {
      el.removeEventListener(eventName, handler)
    })
    this.boundHandlers.clear()
  }

  scheduleRefresh () {
    clearTimeout(this.refreshTimeout)
    this.refreshTimeout = setTimeout(() => {
      this.refreshPreview()
      this.updateConfirmMessage()
    }, this.debounceValue)
  }

  refreshPreview () {
    if (!this.hasFrameTarget) return

    const params = new URLSearchParams()

    this.paramTargets.forEach((el) => {
      const name = el.dataset.turboFrameQueryPreviewNameParam
      if (!name) return

      if (el.type === "checkbox") {
        if (el.checked) params.set(name, el.value || "1")
        return
      }

      const value = el.value
      const omitBlank = el.dataset.turboFrameQueryPreviewOmitBlankParam !== "false"
      if (omitBlank && !String(value ?? "").trim()) return

      params.set(name, value)
    })

    const query = params.toString()
    const nextSrc = query ? `${this.previewUrlValue}?${query}` : this.previewUrlValue

    if (this.normalizePreviewSrc(this.currentPreviewSrc()) === this.normalizePreviewSrc(nextSrc)) return

    this.frameTarget.src = nextSrc
  }

  updateConfirmMessage () {
    if (!this.hasConfirmMessageTarget) return

    const alternateParam = this.alternateWhenParamValue
    const useAlternate = alternateParam && this.paramTargets.some((el) => (
      el.dataset.turboFrameQueryPreviewNameParam === alternateParam && el.checked
    ))

    this.confirmMessageTarget.textContent = useAlternate
      ? this.alternateConfirmMessageValue
      : this.defaultConfirmMessageValue
  }

  eventNameFor (el) {
    const override = el.dataset.turboFrameQueryPreviewEventParam
    if (override) return override

    if (el.type === "checkbox" || el.tagName === "SELECT") return "change"
    return "input"
  }

  currentPreviewSrc () {
    return this.frameTarget.getAttribute("src") || this.frameTarget.src || ""
  }

  normalizePreviewSrc (src) {
    if (!src) return ""

    try {
      const url = new URL(src, window.location.origin)
      return `${url.pathname}${url.search}`
    } catch {
      return src
    }
  }
}
